import { useMemo, useState } from "react";
import { Plus, Palette, Check, AlertCircle, Library } from "lucide-react";
import { useConfigStore, countMissingLightDark } from "../../store/configStore";
import { useEditorStore } from "../../store/editorStore";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import useSWR from "swr";

// 生成随机 ID
function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}

// 从 ColorSet 中提取 light/dark 颜色（兼容新旧格式）
function extractColors(colorSet: any): { light: string; dark: string } {
  // 优先使用新格式
  if (colorSet.light && colorSet.dark) {
    return { light: colorSet.light, dark: colorSet.dark };
  }

  // 兼容旧格式
  if (colorSet.colors && colorSet.colors.length > 0) {
    const sorted = [...colorSet.colors].sort(
      (a: any, b: any) => a.offset - b.offset
    );
    const lightStop = sorted.find((s: any) => s.offset <= 0.1) || sorted[0];
    const darkStop =
      sorted.find((s: any) => s.offset >= 0.9) || sorted[sorted.length - 1];
    return {
      light: lightStop?.color || "#ffffff",
      dark: darkStop?.color || "#000000",
    };
  }

  return { light: "#ffffff", dark: "#000000" };
}

// 渐变预览组件（兼容新旧格式）
function GradientPreview({ colorSet }: { colorSet: any }) {
  const { light, dark } = extractColors(colorSet);

  const gradientStyle = useMemo(
    () => ({
      background: `linear-gradient(to right, ${light} 0%, ${dark} 100%)`,
    }),
    [light, dark]
  );

  return <div className="w-full h-full rounded" style={gradientStyle} />;
}

// 配色项组件
interface ColorSetItemProps {
  colorSet: any;
  isEditing: boolean;
  isSelected: boolean;
  onClick: () => void;
}

function ColorSetItem({
  colorSet,
  isEditing,
  isSelected,
  onClick,
}: ColorSetItemProps) {
  // 检查是否缺少 light/dark 属性（只有旧格式数据）
  const missingLightDark = !colorSet.light || !colorSet.dark;
  // 检查是否有冗余数据（同时有旧格式 colors 和新格式 light/dark）
  const hasLegacyData = colorSet.colors && colorSet.colors.length > 0;
  const hasNewData = colorSet.light && colorSet.dark;
  const hasRedundantData = hasLegacyData && hasNewData;

  // 确定边框警告类型
  const hasWarning = missingLightDark || hasRedundantData;
  const warningTitle = missingLightDark
    ? "缺少 light/dark 属性，请点击编辑"
    : hasRedundantData
      ? "存在冗余旧数据，请点击删除"
      : colorSet.id;

  return (
    <div
      className={cn(
        "relative rounded-lg border p-1.5 cursor-pointer transition-all",
        hasWarning
          ? hasRedundantData && !missingLightDark
            ? "border-amber-500 border-2 bg-amber-50 dark:bg-amber-950/20"
            : "border-red-500 border-2 bg-red-50 dark:bg-red-950/20"
          : isEditing
            ? "bg-primary/20 border-primary ring-2 ring-primary"
            : isSelected
              ? "bg-primary/10 border-primary/50 ring-2 ring-primary/30"
              : "bg-muted/30 hover:bg-muted/50 hover:border-primary/30"
      )}
      onClick={onClick}
      title={warningTitle}
    >
      {/* 渐变预览 */}
      <div className="relative w-full h-10 rounded border overflow-hidden mb-1">
        <GradientPreview colorSet={colorSet} />
        {colorSet.requireAd === 1 && (
          <span className="absolute bottom-0.5 left-0.5 rounded px-1 py-0.5 text-[9px] font-semibold leading-none bg-black/60 text-white">
            AD
          </span>
        )}
      </div>

      {/* ID */}
      <div className="text-center">
        <span className="text-[10px] font-mono text-muted-foreground truncate block">
          {colorSet.id.slice(0, 6)}
        </span>
      </div>

      {/* 选中指示器 */}
      {isSelected && (
        <div className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-emerald-500 flex items-center justify-center">
          <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
        </div>
      )}
    </div>
  );
}

// 调色方案项组件
interface ToningItemProps {
  toning: any;
}

// 统计冗余数据的配色数量
function countRedundantData(toning: any): number {
  if (!toning.colors) return 0;
  return toning.colors.filter(
    (cs: any) => cs.colors?.length > 0 && cs.light && cs.dark
  ).length;
}

function ToningItem({ toning }: ToningItemProps) {
  const {
    editingToningId,
    editingColorSetId,
    setEditingToningId,
    setEditingColorSetId,
    selectedToningColors,
    setToningColor,
    clearToningColor,
    addColorSet,
  } = useEditorStore();

  // 第一个配色用于预览
  const previewColorSet = toning.colors[0];
  // 当前选中的配色 ID（不使用 fallback，允许为空）
  const currentColorId = selectedToningColors[toning.id];
  // 统计缺少 light/dark 属性的配色数量（使用集中校验）
  const missingLightDarkCount = countMissingLightDark(toning);
  // 统计冗余数据的配色数量
  const redundantDataCount = countRedundantData(toning);
  const handleAddColorSet = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newColorSet: any = {
      id: generateId(),
      light: "#ffffff",
      dark: "#000000",
    };
    addColorSet(toning.id, newColorSet);
    // 自动选中新建的配色
    setEditingColorSetId(toning.id, newColorSet.id);
  };

  return (
    <AccordionItem
      value={toning.id}
      className={cn(
        "border rounded-lg mb-2",
        missingLightDarkCount > 0
          ? "border-red-500 border-2"
          : redundantDataCount > 0
            ? "border-amber-500 border-2"
            : ""
      )}
    >
      <AccordionTrigger className="px-3 py-2 hover:no-underline">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* 渐变预览 */}
          <div className="w-10 h-6 rounded border overflow-hidden flex-shrink-0">
            {previewColorSet && <GradientPreview colorSet={previewColorSet} />}
          </div>

          {/* 信息 */}
          <div className="flex-1 min-w-0 text-left">
            <div className="flex items-center gap-2">
              <Palette className="h-3.5 w-3.5 text-primary flex-shrink-0" />
              <span className="font-medium text-sm truncate">
                {toning.name || toning.id}
              </span>
            </div>
            <span className="text-[10px] text-muted-foreground font-mono">
              {toning.id}
            </span>
          </div>

          {/* 配色数量 */}
          <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
            {toning.colors.length} 配色
          </Badge>

          {/* 缺少 light/dark 警告 */}
          {missingLightDarkCount > 0 && (
            <div
              className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center mr-2"
              title={`${missingLightDarkCount} 个配色缺少 light/dark 属性`}
            >
              <AlertCircle className="h-3 w-3 text-white" />
            </div>
          )}

          {/* 冗余数据警告 */}
          {redundantDataCount > 0 && missingLightDarkCount === 0 && (
            <div
              className="h-5 w-5 rounded-full bg-amber-500 flex items-center justify-center mr-2"
              title={`${redundantDataCount} 个配色有冗余旧数据`}
            >
              <AlertCircle className="h-3 w-3 text-white" />
            </div>
          )}

          {/* 详情按钮 */}
          <Button
            variant={
              editingToningId === toning.id && !editingColorSetId
                ? "default"
                : "ghost"
            }
            size="sm"
            className="h-6 px-2 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              setEditingToningId(toning.id);
            }}
          >
            详情
          </Button>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-3 pb-3">
        {/* 配色列表 - 网格布局 */}
        <div className="grid grid-cols-4 gap-2">
          {/* 添加配色卡片 */}
          <div
            className="group rounded-lg border border-dashed border-muted-foreground/30 p-1.5 cursor-pointer transition-all hover:border-primary hover:bg-muted/30 flex flex-col items-center justify-center"
            onClick={handleAddColorSet}
          >
            <div className="w-full h-10 rounded flex items-center justify-center">
              <Plus className="h-5 w-5 text-muted-foreground group-hover:text-primary" />
            </div>
            <span className="text-[10px] text-muted-foreground group-hover:text-primary">
              添加
            </span>
          </div>

          {toning.colors.map((colorSet) => (
            <ColorSetItem
              key={colorSet.id}
              colorSet={colorSet}
              isEditing={
                editingToningId === toning.id &&
                editingColorSetId === colorSet.id
              }
              isSelected={currentColorId === colorSet.id}
              onClick={() => {
                setEditingColorSetId(toning.id, colorSet.id);
                // Toggle 预览：如果已选中则取消，否则选中
                if (currentColorId === colorSet.id) {
                  clearToningColor(toning.id);
                  if (useEditorStore.getState().previewToningId === toning.id) {
                    useEditorStore.getState().setPreviewToningId(null);
                  }
                } else {
                  setToningColor(toning.id, colorSet.id);
                  useEditorStore.getState().setPreviewToningId(toning.id);
                }
              }}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

// 全局调色方案渐变预览（用于选择列表）
function GlobalToningPreview({ toning }: { toning: any }) {
  const firstColor = toning.colors?.[0];
  if (!firstColor) {
    return <div className="w-full h-full bg-muted rounded" />;
  }
  const light = firstColor.light || "#ffffff";
  const dark = firstColor.dark || "#000000";
  return (
    <div
      className="w-full h-full rounded"
      style={{
        background: `linear-gradient(to right, ${light} 0%, ${dark} 100%)`,
      }}
    />
  );
}

// 全局调色方案选择对话框
function GlobalToningPicker({
  onSelect,
  existingIds,
}: {
  onSelect: (toning: any) => void;
  existingIds: string[];
}) {
  const [open, setOpen] = useState(false);
  const { data: globalTonings, isLoading } = useSWR<any[]>(
    open ? ["/spine-tonings"] : null
  );

  // 过滤掉已添加的
  const availableTonings = (globalTonings || []).filter(
    (t) => !existingIds.includes(t.id)
  );

  const handleSelect = (toning: any) => {
    onSelect(toning);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full shrink-0" size="sm">
          <Library className="h-4 w-4 mr-2" />
          从全局库选择
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[70vh]">
        <DialogHeader>
          <DialogTitle>选择全局调色方案</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[50vh] pr-4">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              加载中...
            </div>
          ) : availableTonings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {globalTonings?.length === 0
                ? "暂无全局调色方案"
                : "所有调色方案都已添加"}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {availableTonings.map((toning) => (
                <div
                  key={toning.id}
                  className="border rounded-lg p-3 cursor-pointer hover:border-primary hover:bg-muted/30 transition-all"
                  onClick={() => handleSelect(toning)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-8 rounded border overflow-hidden flex-shrink-0">
                      <GlobalToningPreview toning={toning} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">
                        {toning.name || toning.id}
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono">
                        {toning.id}
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-[10px]">
                      {toning.colors?.length || 0} 配色
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export function ToningEditor() {
  const { templateConfig } = useConfigStore();
  const { setEditingToningId, addToning } = useEditorStore();

  const handleAddToning = () => {
    const toningCount = templateConfig?.tonings?.length || 0;
    const newToning: any = {
      id: generateId(),
      name: `调色方案 ${toningCount + 1}`,
      pro: 0,
      colors: [
        {
          id: generateId(),
          light: "#ffffff",
          dark: "#000000",
        },
      ],
      _isNew: true, // 标记为新建，保存时需要同步到全局库
    };
    addToning(newToning);
    // 自动选中新建的方案
    setEditingToningId(newToning.id);
  };

  const handleSelectGlobalToning = (toning: any) => {
    // 从全局库选择的调色方案直接添加
    addToning({
      ...toning,
      _isNew: false, // 标记为已存在于全局库
    });
    setEditingToningId(toning.id);
  };

  if (!templateConfig) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
        暂无数据
      </div>
    );
  }

  const tonings = templateConfig.tonings || [];
  const existingToningIds = tonings.map((t: any) => t.id);

  // 统计所有 toning 中的错误数量
  const totalMissingLightDark = tonings.reduce(
    (sum: number, t: any) => sum + countMissingLightDark(t),
    0
  );
  const totalRedundantData = tonings.reduce(
    (sum: number, t: any) => sum + countRedundantData(t),
    0
  );

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* 边框说明（图例） */}
      {(totalMissingLightDark > 0 || totalRedundantData > 0) && (
        <div className="flex flex-wrap gap-3 px-3 py-2 rounded-lg bg-muted/50 border text-xs shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border-2 border-red-500 bg-red-50 dark:bg-red-950/30" />
            <span className="text-muted-foreground">
              缺少 light/dark
              {totalMissingLightDark > 0 && (
                <span className="text-red-600 dark:text-red-400 ml-1">
                  ({totalMissingLightDark})
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/30" />
            <span className="text-muted-foreground">
              冗余旧数据
              {totalRedundantData > 0 && (
                <span className="text-amber-600 dark:text-amber-400 ml-1">
                  ({totalRedundantData})
                </span>
              )}
            </span>
          </div>
        </div>
      )}

      {/* 添加按钮区域 */}
      <div className="flex gap-2 shrink-0">
        <Button onClick={handleAddToning} className="flex-1" size="sm">
          <Plus className="h-4 w-4 mr-2" />
          新建调色方案
        </Button>
        <GlobalToningPicker
          onSelect={handleSelectGlobalToning}
          existingIds={existingToningIds}
        />
      </div>

      {/* 列表 */}
      <ScrollArea className="flex-1">
        {tonings.length === 0 ? (
          <div className="text-center text-muted-foreground text-sm py-8">
            暂无调色方案，点击上方按钮添加
          </div>
        ) : (
          <Accordion type="single" collapsible className="pr-3">
            {tonings.map((toning: any) => (
              <ToningItem key={toning.id} toning={toning} />
            ))}
          </Accordion>
        )}
      </ScrollArea>
    </div>
  );
}
